// @file Pure functions that pick the role, sequences and on-screen actions for the
// recommended actions card. All padlet.tv URLs come from the Airtable rows served
// by the padlet-tv-sessions endpoint.
import type { MatchedGalleryFilters } from '@@/bits/dash_gallery_helper'
import { p__, __ } from '@@/bits/intl'
import { UserRole } from '@@/bits/user_model'
import { RecommendedActionsCardExperimentVariant, UserAccountType } from '@@/enums'
import type { LearnVideo } from '@@/pinia/dash_learn_store'
import type { WallGalleryTemplate } from '@@/types'
import { Audience } from '@@/types/dash'

type RecommendedActionsRoleSource = 'account_type' | 'tenant_role' | 'library_access' | 'fallback'

// Mirrors the attributes PadletTv::Sessions.normalize sends.
interface PadletTvSession {
  id: string
  title: string
  durationMinutes: number | null
  url: string
  thumbnailUrl: string | null
  roles: string[]
  grades: string[]
  level: string[]
  language: string | null
  kind: string | null
  priority: number | null
  sessionDate: string | null
}

type RecommendedAction =
  | { kind: 'padlet_tv'; key: string; session: PadletTvSession }
  // `video` is null when Learn data hasn't loaded; the link then falls back to the Padlet 101 section.
  | { kind: 'learn_video'; key: string; video: LearnVideo | null }
  | { kind: 'learn_page'; key: 'learn_page' }
  | { kind: 'gallery_template'; key: string; template: WallGalleryTemplate }
  | { kind: 'browse_gallery'; key: 'browse_gallery' }

interface PinnedPdAction {
  kind: 'pd_course'
  key: string
  title: string
  subtitle: string
  url: string
}

interface ChosenRecommendedActions {
  featured: RecommendedAction
  moreToTry: RecommendedAction[]
  // Whether a reload can feature something else.
  canReload: boolean
  // The key to send as the cursor when the user reloads.
  nextCursor: string
}

const PD_COURSE_KIND = 'PD course'
const POPULAR_CATEGORY = 'Popular'
const SESSION_LEVELS = ['beginner', 'intermediate', 'advanced']
const SESSION_LANGUAGE_BY_LANG_PREFIX: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  pt: 'Portuguese',
  ja: 'Japanese',
}

function resolveRecommendedActionsRole({
  accountType,
  userRole,
  hasTeacherLevelAccessInAnyLibrary,
}: {
  accountType?: UserAccountType | null
  userRole?: string | null
  hasTeacherLevelAccessInAnyLibrary: boolean
}): { role: UserAccountType; roleSource: RecommendedActionsRoleSource } {
  if (accountType != null) return { role: accountType, roleSource: 'account_type' }
  if (userRole === UserRole.TEACHER) return { role: UserAccountType.TEACHER, roleSource: 'tenant_role' }
  if (userRole === UserRole.ADMIN || userRole === UserRole.OWNER) {
    return { role: UserAccountType.STAFF, roleSource: 'tenant_role' }
  }
  if (userRole === UserRole.STUDENT) return { role: UserAccountType.STUDENT, roleSource: 'tenant_role' }
  if (hasTeacherLevelAccessInAnyLibrary) return { role: UserAccountType.TEACHER, roleSource: 'library_access' }
  return { role: UserAccountType.PERSONAL, roleSource: 'fallback' }
}

function audienceForRole(role: UserAccountType): Audience {
  switch (role) {
    case UserAccountType.TEACHER:
    case UserAccountType.STAFF:
    case UserAccountType.STUDENT:
      return Audience.Education
    case UserAccountType.BUSINESS:
      return Audience.Business
    default:
      return Audience.General
  }
}

// Maps a UI language ("en", "pt-BR", "ja_JP") to the Airtable `language` value, or null when no
// sessions are recorded in it.
function sessionLanguageForLang(lang: string | null | undefined): string | null {
  const prefix = (lang ?? '').toLowerCase().split(/[-_]/)[0]
  return SESSION_LANGUAGE_BY_LANG_PREFIX[prefix] ?? null
}

// Starts the list at item `start % length` and wraps around, keeping every item once.
function rotateFrom<T>(items: T[], start: number): T[] {
  if (items.length === 0) return items
  const offset = Math.abs(start) % items.length
  return [...items.slice(offset), ...items.slice(0, offset)]
}

function overlaps(a: string[] | undefined, b: string[]): boolean {
  return (a ?? []).some((value) => b.includes(value))
}

function lowestLevelIndex(session: PadletTvSession): number {
  const indexes = session.level.map((level) => SESSION_LEVELS.indexOf(level)).filter((index) => index !== -1)
  return indexes.length === 0 ? -1 : Math.min(...indexes)
}

function compareSessions(a: PadletTvSession, b: PadletTvSession): number {
  if (a.priority !== b.priority) {
    if (a.priority == null) return 1
    if (b.priority == null) return -1
    return a.priority - b.priority
  }
  // ISO dates sort as strings; newest first, undated last.
  return (b.sessionDate ?? '').localeCompare(a.sessionDate ?? '')
}

// Get started, then the padlet.tv sessions that fit the user, then the remaining Learn videos in the
// order the Learn page lists them, then the Learn card. Learn videos have narration in English but
// might have captions in the user's language, so users with few or no padlet.tv sessions in their
// language still get plenty of Learn videos to rotate through.
function videoSequence(
  sessions: PadletTvSession[],
  getStartedVideo: LearnVideo | null,
  learnVideos: LearnVideo[],
  role: UserAccountType,
  matchedGrades: string[],
  userLanguage: string | null,
  userId: number,
): RecommendedAction[] {
  const gradeFilter = role === UserAccountType.TEACHER ? matchedGrades : []
  const fitting = sessions.filter(
    (session) =>
      session.kind !== PD_COURSE_KIND &&
      session.roles.includes(role) &&
      userLanguage != null &&
      session.language === userLanguage &&
      (gradeFilter.length === 0 || session.grades.length === 0 || overlaps(session.grades, gradeFilter)),
  )
  // One tier per level, then a last tier for sessions with no recognised level (a data entry slip),
  // so a published session never drops out. Each user starts a tier at item `userId % length`, so users see
  // different sessions first; ids count up by 1 within a shard, so consecutive signups start on consecutive ones.
  const tierIndex = (session: PadletTvSession): number => {
    const index = lowestLevelIndex(session)
    return index === -1 ? SESSION_LEVELS.length : index
  }
  const tiers = [...SESSION_LEVELS, 'unlevelled'].map((_, index) =>
    rotateFrom(fitting.filter((session) => tierIndex(session) === index).sort(compareSessions), userId),
  )
  return [
    // Keyed by its Learn anchor, not the video ID, so completing it before Learn data loads still counts after.
    { kind: 'learn_video', key: 'learn:padlet-101', video: getStartedVideo },
    ...tiers
      .flat()
      .map((session): RecommendedAction => ({ kind: 'padlet_tv', key: `padlet_tv:${session.id}`, session })),
    ...learnVideos
      .filter((video) => video.video.videoId !== getStartedVideo?.video.videoId)
      .map(
        (video): RecommendedAction => ({
          kind: 'learn_video',
          key: learnVideoActionKey(video, getStartedVideo),
          video,
        }),
      ),
    { kind: 'learn_page', key: 'learn_page' },
  ]
}

// Orders every template for the role's audience, without dropping any, in three tiers laid end to end:
// - R1: matches every non-blank filter (grade and subject)
// - R2: matches either
// - R3: the rest
// Each tier is Popular first, then the rest, each rotated per user. A teacher with few matches moves on to R2 and R3
// instead of cycling through the same few templates. Returns a single "Browse Gallery" card when there
// are no templates, i.e. the Gallery fetch failed.
function remakeSequence(
  templates: WallGalleryTemplate[],
  role: UserAccountType,
  matchedFilters: MatchedGalleryFilters,
  userId: number,
): RecommendedAction[] {
  const audience = audienceForRole(role)
  const activeFilters = [
    { values: matchedFilters.grades, field: (t: WallGalleryTemplate) => t.grades },
    { values: matchedFilters.subjects, field: (t: WallGalleryTemplate) => t.category },
  ].filter((filter) => filter.values.length > 0)
  const tiers: WallGalleryTemplate[][] = [[], [], []]
  // The Gallery opens the first template with an id in any audience, so a repeat would only share its key, and an
  // id whose first template is another audience's would open that one instead.
  const seenIds = new Set<string>()
  const isFirstWithId = (template: WallGalleryTemplate): boolean => {
    if (seenIds.has(template.galleryTemplateId)) return false
    seenIds.add(template.galleryTemplateId)
    return true
  }
  templates
    .filter(isFirstWithId)
    .filter((template) => template.audience === audience)
    .forEach((template) => {
      const matches = activeFilters.filter((filter) => overlaps(filter.field(template), filter.values)).length
      if (activeFilters.length > 0 && matches === activeFilters.length) tiers[0].push(template)
      else if (matches > 0) tiers[1].push(template)
      else tiers[2].push(template)
    })
  const isPopular = (t: WallGalleryTemplate): boolean => (t.category ?? []).includes(POPULAR_CATEGORY)
  // Each user starts at item `userId % length`, as for sessions. Rotate Popular and the rest separately, so Popular
  // stays first whatever the user's start.
  const ordered = tiers.flatMap((tier) => [
    ...rotateFrom(tier.filter(isPopular), userId),
    ...rotateFrom(
      tier.filter((t) => !isPopular(t)),
      userId,
    ),
  ])
  if (ordered.length === 0) return [{ kind: 'browse_gallery', key: 'browse_gallery' }]
  return ordered.map((template) => ({
    kind: 'gallery_template',
    key: galleryTemplateActionKey(template),
    template,
  }))
}

// The key a Gallery template's action is completed under. Rails builds the same key when a padlet is created from
// the template (RecommendedActions.record_gallery_template_creation).
function galleryTemplateActionKey(template: Pick<WallGalleryTemplate, 'galleryTemplateId'>): string {
  return `gallery:${template.galleryTemplateId}`
}

// The key a Learn video's action is completed under. videoSequence and the Learn video player both build it here,
// so they always match. Get started is keyed by its Learn anchor, as in videoSequence.
function learnVideoActionKey(video: LearnVideo, getStartedVideo: LearnVideo | null | undefined): string {
  if (video.video.videoId === getStartedVideo?.video.videoId) return 'learn:padlet-101'
  return `learn:${video.video.videoId}`
}

function pinnedPdAction(sessions: PadletTvSession[], role: UserAccountType): PinnedPdAction | null {
  if (role !== UserAccountType.TEACHER && role !== UserAccountType.STAFF) return null
  const pdSession = sessions.find((session) => session.kind === PD_COURSE_KIND && session.roles.includes(role))
  if (pdSession == null) return null
  return {
    kind: 'pd_course',
    key: `padlet_tv:${pdSession.id}`,
    title:
      role === UserAccountType.TEACHER
        ? p__(
            'Title of a card linking to free online Padlet training courses for teachers that award a certificate on completion',
            'Get Padlet certified',
          )
        : __('Offer professional development to your teachers'),
    subtitle: __('Take free self-paced courses'),
    url: pdSession.url,
  }
}

// Links to whole pages, which only ever show in More to try.
const UNFEATURABLE_KINDS: Array<RecommendedAction['kind']> = ['learn_page', 'browse_gallery']
const isFeaturable = (action: RecommendedAction): boolean => !UNFEATURABLE_KINDS.includes(action.kind)

// Leaves out the actions the user has completed, so the next one in the sequence takes their place.
// Once every featurable action is completed, starts the sequence over rather than leave the card empty.
function withoutCompleted(sequence: RecommendedAction[], completedKeys: string[]): RecommendedAction[] {
  const remaining = sequence.filter((action) => !completedKeys.includes(action.key))
  return remaining.some(isFeaturable) ? remaining : sequence
}

// Picks the card's actions from the ones the user hasn't completed yet (`completedKeys`: a template they
// made a padlet from, a Learn video they watched to the end, or a recording they opened), so the card only
// changes when the user completes an action or reloads, never because a day passed. "Preferred" is the arm's
// featured kind (videos for video_first, remakes for gallery_first).
// - featured: the first action not completed at or after `cursor` (the action the last reload featured,
//   the start of the sequence before any reload) among the preferred sequence's featurable items,
//   wrapping from the last video back to Get started. Completing an action doesn't move the cursor, so
//   the card moves past only the actions the user completes.
// - moreToTry[0] (slot 1): the other kind's first item, so both kinds are always on screen; it ignores
//   reloads.
// - moreToTry[1] (slot 2): the item after the featured one in its full sequence, so the Learn card
//   shows here after the last video.
// - nextCursor: the action a reload features, the next one not completed after the featured one.
// With no featurable remakes (only Browse Gallery), gallery_first features a video instead, with
// Browse Gallery in slot 1. Returns null for the control arm, which shows no card.
function chooseRecommendedActions({
  variant,
  videoSequence,
  remakeSequence,
  cursor,
  completedKeys,
}: {
  variant: RecommendedActionsCardExperimentVariant
  videoSequence: RecommendedAction[]
  remakeSequence: RecommendedAction[]
  cursor: string | null
  completedKeys: string[]
}): ChosenRecommendedActions | null {
  if (variant === RecommendedActionsCardExperimentVariant.Control) return null
  const [preferredFull, otherFull] =
    variant === RecommendedActionsCardExperimentVariant.VideoFirst
      ? [videoSequence, remakeSequence]
      : [remakeSequence, videoSequence]
  const preferred = withoutCompleted(preferredFull, completedKeys)
  const other = withoutCompleted(otherFull, completedKeys)
  const at = (sequence: RecommendedAction[], index: number): RecommendedAction => sequence[index % sequence.length]
  const [featuredFull, featuredSource, slot1Source] = preferred.some(isFeaturable)
    ? [preferredFull, preferred, other]
    : [otherFull, other, preferred]
  const featurable = featuredSource.filter(isFeaturable)
  // Searched in the full sequence, which completing an action doesn't change. A cursor that's no longer in
  // it, e.g. a session taken off padlet.tv, starts from the beginning.
  const fullFeaturable = featuredFull.filter(isFeaturable)
  const isNotCompleted = (action: RecommendedAction): boolean => !completedKeys.includes(action.key)
  const cursorIndex = fullFeaturable.findIndex((action) => action.key === cursor)
  const fromCursor = rotateFrom(fullFeaturable, Math.max(cursorIndex, 0))
  const featured = fromCursor.find(isNotCompleted) ?? fromCursor[0]
  const afterFeatured = rotateFrom(fullFeaturable, fullFeaturable.indexOf(featured) + 1)
  const next = afterFeatured.find((action) => isNotCompleted(action) && action.key !== featured.key) ?? afterFeatured[0]
  const slot1 = slot1Source[0]
  // If the featured source has only one item, the next item would be the featured one again, so slot 2
  // takes the item after slot 1 from the other kind instead.
  const slot2 =
    featuredSource.length > 1 ? at(featuredSource, featuredSource.indexOf(featured) + 1) : at(slot1Source, 1)
  const moreToTry = slot2.key === slot1.key || slot2.key === featured.key ? [slot1] : [slot1, slot2]
  return { featured, moreToTry, canReload: featurable.length > 1, nextCursor: next.key }
}

export {
  chooseRecommendedActions,
  galleryTemplateActionKey,
  learnVideoActionKey,
  pinnedPdAction,
  POPULAR_CATEGORY,
  remakeSequence,
  resolveRecommendedActionsRole,
  audienceForRole,
  rotateFrom,
  sessionLanguageForLang,
  videoSequence,
}
export type { PadletTvSession, PinnedPdAction, RecommendedAction, ChosenRecommendedActions }
